import { useState } from 'react';
import { makeStyles } from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';

import TabsController from '../../../shared/components/Tabs/ButtonTabsController';
import HomeDeliveryNoteCard from '../Cards/HomeDeliveryNoteCard.component';
import DeliveryPointDeliveryNoteCard from '../Cards/DeliveryPointDeliveryNoteCard.component';
import EcoDeliveryNoteCard from '../Cards/EcoDeliveryNoteCard.component';
import { DeliveryNote, DeliveryNoteTypes } from '../../types/delivery-note.type';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },

  section: {
    '& h3': {
      margin: '0 0 0.75rem 0',
    },
  },
});

type Props = {
  deliveryNotes: DeliveryNote[] | null;
  loading: boolean;
};

type DeliveryNotesSection = {
  id: string;
  title: string;
  tabLabel: string;
  emptyMessage: string;
  cards: JSX.Element[];
};

export default function DeliveryNotesList({ deliveryNotes, loading }: Props) {
  const classes = useStyles();

  const [currentTab, setCurrentTab] = useState('all');

  if (loading) return <Skeleton variant="rect" width="100%" height={250} />;
  if (!deliveryNotes) return <p>Aucun bon de livraison à afficher</p>;

  const sections: DeliveryNotesSection[] = [
    {
      id: 'home',
      title: 'Livraisons à domicile',
      tabLabel: 'À domicile',
      emptyMessage: 'Aucune livraison à domicile',
      cards: deliveryNotes
        .filter((note) => note.type === DeliveryNoteTypes.HOME)
        .map((note) => <HomeDeliveryNoteCard key={note.orders[0].id} note={note} />),
    },
    {
      id: 'delivery-point',
      title: 'Points de livraison',
      tabLabel: 'Point de livraison',
      emptyMessage: 'Aucune livraison en point de livraison',
      cards: deliveryNotes
        .filter((note) => note.type === DeliveryNoteTypes.DELIVERY_POINT)
        .map((note) => <DeliveryPointDeliveryNoteCard key={note.orders[0].id} note={note} />),
    },
    {
      id: 'eco',
      title: 'Éco-acteurs',
      tabLabel: 'Éco-acteur',
      emptyMessage: 'Aucune livraison éco-acteur',
      cards: deliveryNotes
        .filter((note) => note.type === DeliveryNoteTypes.ECO)
        .map((note) => <EcoDeliveryNoteCard key={note.orders[0].id} note={note} />),
    },
  ];

  const totalCount = sections.reduce((total, section) => total + section.cards.length, 0);

  const tabs = [
    { id: 'all', label: 'Tous', value: 'all', count: totalCount },
    ...sections.map((section) => ({
      id: section.id,
      label: section.tabLabel,
      value: section.id,
      count: section.cards.length,
    })),
  ];

  const visibleSections = sections.filter((section) => currentTab === 'all' || section.id === currentTab);
  const isEverythingEmpty = sections.every((section) => section.cards.length === 0);

  return (
    <section className={classes.root}>
      <TabsController size="small" tabs={tabs} currentTabId={currentTab} onTabChange={setCurrentTab} />

      {isEverythingEmpty && <p>Aucun bon de livraison pour cette date</p>}

      {!isEverythingEmpty &&
        visibleSections.map((section) => (
          <section key={section.id} className={classes.section}>
            <h3>{section.title}</h3>
            {section.cards.length === 0 && <p>{section.emptyMessage}</p>}
            {section.cards}
          </section>
        ))}
    </section>
  );
}
